import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { Search, AlertTriangle, CheckCircle, Clock, Activity, Shield } from 'lucide-react';

const AuditPanel = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const auditMetrics = [
    {
      title: "Total Audits",
      value: "248",
      period: "Last 30 Days",
      breakdown: [
        { name: "Automated", value: 186 },
        { name: "Manual", value: 62 }
      ]
    },
    {
      title: "Issue Resolution",
      value: "98.2%",
      period: "Average",
      breakdown: [
        { name: "Critical", value: "100%" },
        { name: "High", value: "98.5%" }
      ]
    },
    {
      title: "System Coverage",
      value: "100%",
      period: "All Systems",
      breakdown: [
        { name: "Neural Lab", value: "100%" },
        { name: "Content", value: "100%" }
      ]
    }
  ];

  const recentAudits = [
    {
      system: "Neural Lab",
      type: "Model Validation",
      findings: 2,
      status: "Complete",
      date: "2025-03-18",
      resolution: "100%"
    },
    {
      system: "Content Pipeline",
      type: "Data Flow",
      findings: 1,
      status: "In Progress",
      date: "2025-03-19",
      resolution: "75%"
    },
    {
      system: "Crowdsourcing",
      type: "Access Control",
      findings: 0,
      status: "Complete",
      date: "2025-03-17",
      resolution: "100%"
    }
  ];

  const auditFindings = [
    {
      id: "F-2025-042",
      severity: "Medium",
      description: "Content adaptation latency above threshold",
      system: "Content Pipeline",
      status: "In Progress",
      dueDate: "2025-03-25"
    },
    {
      id: "F-2025-041",
      severity: "Low",
      description: "Model validation documentation update needed",
      system: "Neural Lab",
      status: "Resolved",
      dueDate: "2025-03-20"
    },
    {
      id: "F-2025-040",
      severity: "High",
      description: "Access control policy refinement required",
      system: "Crowdsourcing",
      status: "Resolved",
      dueDate: "2025-03-18"
    }
  ];

  return (
    <VStack spacing={6} align="stretch">
      {/* Audit Metrics */}
      <SimpleGrid columns={3} spacing={4}>
        {auditMetrics.map((metric, index) => (
          <Box
            key={index}
            bg={bgColor}
            p={4}
            borderRadius="md"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <VStack align="start" spacing={3}>
              <HStack>
                <Icon
                  as={index === 0 ? Search : index === 1 ? CheckCircle : Shield}
                  color="methodist.blue"
                  boxSize={5}
                />
                <Text fontSize="sm" fontWeight="medium">{metric.title}</Text>
              </HStack>
              <Stat>
                <StatNumber fontSize="2xl" color="methodist.blue">
                  {metric.value}
                </StatNumber>
                <StatLabel fontSize="xs" color="gray.500">
                  {metric.period}
                </StatLabel>
              </Stat>
              <Box width="100%">
                <SimpleGrid columns={2} spacing={2}>
                  {metric.breakdown.map((item, idx) => (
                    <HStack key={idx} justify="space-between">
                      <Text fontSize="xs" color="gray.500">{item.name}</Text>
                      <Text fontSize="xs" fontWeight="medium">{item.value}</Text>
                    </HStack>
                  ))}
                </SimpleGrid>
              </Box>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Recent Audits */}
      <Box
        bg={bgColor}
        p={4}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Text fontSize="lg" fontWeight="medium" mb={4}>Recent Audits</Text>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>System</Th>
              <Th>Type</Th>
              <Th>Findings</Th>
              <Th>Status</Th>
              <Th>Date</Th>
              <Th>Resolution</Th>
            </Tr>
          </Thead>
          <Tbody>
            {recentAudits.map((audit, index) => (
              <Tr key={index}>
                <Td>
                  <Text fontWeight="medium">{audit.system}</Text>
                </Td>
                <Td>{audit.type}</Td>
                <Td>
                  <Badge
                    colorScheme={
                      audit.findings === 0 ? 'green' :
                      audit.findings <= 2 ? 'yellow' :
                      'red'
                    }
                  >
                    {audit.findings}
                  </Badge>
                </Td>
                <Td>
                  <Badge
                    colorScheme={
                      audit.status === 'Complete' ? 'green' : 'orange'
                    }
                  >
                    {audit.status}
                  </Badge>
                </Td>
                <Td>{audit.date}</Td>
                <Td>
                  <HStack spacing={2}>
                    <Progress
                      value={parseInt(audit.resolution)}
                      size="sm"
                      width="60px"
                      colorScheme={parseInt(audit.resolution) === 100 ? 'green' : 'blue'}
                    />
                    <Text fontSize="xs">{audit.resolution}</Text>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* Audit Findings */}
      <Box
        bg={bgColor}
        p={4}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Text fontSize="lg" fontWeight="medium" mb={4}>Active Findings</Text>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Severity</Th>
              <Th>Description</Th>
              <Th>System</Th>
              <Th>Status</Th>
              <Th>Due Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {auditFindings.map((finding, index) => (
              <Tr key={index}>
                <Td>
                  <Text fontSize="sm" fontFamily="mono">{finding.id}</Text>
                </Td>
                <Td>
                  <Badge
                    colorScheme={
                      finding.severity === 'High' ? 'red' :
                      finding.severity === 'Medium' ? 'orange' :
                      'yellow'
                    }
                  >
                    {finding.severity}
                  </Badge>
                </Td>
                <Td>
                  <Text fontSize="sm">{finding.description}</Text>
                </Td>
                <Td>{finding.system}</Td>
                <Td>
                  <Badge
                    colorScheme={
                      finding.status === 'Resolved' ? 'green' : 'orange'
                    }
                  >
                    {finding.status}
                  </Badge>
                </Td>
                <Td>{finding.dueDate}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
};

export default AuditPanel;
